import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TranslationModule } from '@core/translation/translation.module';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { BcmSettingsSectionName } from '@shared/models/bcm-settings';
import {
    BerthOnlineBookingQrInfo,
    OnlineBookingApiService,
} from '@modules/bcm/@shared/services/api-services/online-booking-api.service';
import { FilesApiService } from '@bcmApiServices/files.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';

@Component({
    selector: 'berth-online-booking-qr-panel',
    templateUrl: './berth-online-booking-qr-panel.component.html',
    styleUrls: ['./berth-online-booking-qr-panel.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSlideToggleModule,
        TranslationModule,
    ],
})
export class BerthOnlineBookingQrPanelComponent implements OnInit, OnChanges, OnDestroy {

    @Input() berthId?: number;
    @Input() berthHandle?: string;

    loading = false;
    generating = false;
    savingStickerOptions = false;
    qrStickerIncludeLogo = false;
    qrStickerIncludeCaption = false;
    qrInfo?: BerthOnlineBookingQrInfo;
    qrImageUrl?: string;

    private blobSubscription?: Subscription;

    constructor(
        private _onlineBookingApi: OnlineBookingApiService,
        private _filesApi: FilesApiService,
        private _appNotification: AppNotificationService,
        private _tolgee: TolgeeService,
        private _confirmDialogService: ConfirmDialogService,
        private _settingsFacade: BcmSettingsFacade,
    ) {
    }

    ngOnInit(): void {
        this.loadStickerSettings();
        this.loadQrInfo();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.berthId && !changes.berthId.firstChange) {
            this.loadQrInfo();
        }
    }

    ngOnDestroy(): void {
        this.blobSubscription?.unsubscribe();
        this.revokeQrImageUrl();
    }

    loadQrInfo(): void {
        if (!this.berthId) {
            this.qrInfo = undefined;
            this.revokeQrImageUrl();
            return;
        }

        this.loading = true;
        this._onlineBookingApi.getBerthQrCode(this.berthId).subscribe({
            next: info => {
                this.qrInfo = info;
                this.loading = false;
                this.loadQrImage(info.qrCodeFileId);
            },
            error: err => {
                this.loading = false;
                this._appNotification.showError(err?.error?.message || this._tolgee.translate('common.checkYourEntries'));
            },
        });
    }

    onQrStickerIncludeLogoChange(checked: boolean): void {
        this.qrStickerIncludeLogo = checked;
        this.saveStickerOption('qrStickerIncludeLogo', checked);
    }

    onQrStickerIncludeCaptionChange(checked: boolean): void {
        this.qrStickerIncludeCaption = checked;
        this.saveStickerOption('qrStickerIncludeCaption', checked);
    }

    generateQr(): void {
        if (!this.berthId) {
            return;
        }

        if (this.qrInfo?.qrCodeFileId) {
            this._confirmDialogService
                .setTitle(this._tolgee.translate('bcmBerths.onlineBookingQr.regenerateConfirmTitle'))
                .setBody(this._tolgee.translate('bcmBerths.onlineBookingQr.regenerateConfirmMessage'))
                .setYesButton({ text: this._tolgee.translate('bcmBerths.onlineBookingQr.regenerateConfirmYes') })
                .setNoButton({ text: this._tolgee.translate('common.cancel') })
                .useWarnTheme()
                .openAndReturnResult<boolean>()
                .pipe(take(1))
                .subscribe(confirmed => {
                    if (confirmed) {
                        this.executeGenerateQr();
                    }
                });

            return;
        }

        this.executeGenerateQr();
    }

    copyUrl(): void {
        if (!this.qrInfo?.publicUrl) {
            return;
        }

        navigator.clipboard.writeText(this.qrInfo.publicUrl).then(() => {
            this._appNotification.showSuccess(this._tolgee.translate('bcmBerths.onlineBookingQr.urlCopied'));
        });
    }

    downloadQr(): void {
        if (!this.qrInfo?.qrCodeFileId) {
            return;
        }

        this._filesApi.getById(this.qrInfo.qrCodeFileId).subscribe(blob => {
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `liegeplatz-${this.berthHandle || this.berthId}-qr.png`;
            anchor.click();
            URL.revokeObjectURL(url);
        });
    }

    private loadStickerSettings(): void {
        const applySettings = () => {
            const onlineBooking = this._settingsFacade.settings()?.onlineBooking;
            this.qrStickerIncludeLogo = !!onlineBooking?.qrStickerIncludeLogo;
            this.qrStickerIncludeCaption = !!onlineBooking?.qrStickerIncludeCaption;
        };

        if (BcmSettingsFacade.lastSettingsState?.onlineBooking) {
            applySettings();
            return;
        }

        this._settingsFacade.loadSettings().pipe(take(1)).subscribe(() => applySettings());
    }

    private saveStickerOption(
        key: 'qrStickerIncludeLogo' | 'qrStickerIncludeCaption',
        value: boolean,
    ): void {
        this.savingStickerOptions = true;
        this._settingsFacade.updateSectionItem(
            BcmSettingsSectionName.OnlineBooking,
            key,
            value,
            true,
        ).add(() => {
            this.savingStickerOptions = false;
        });
    }

    private executeGenerateQr(): void {
        this.generating = true;
        this._onlineBookingApi.generateBerthQrCode(this.berthId).subscribe({
            next: info => {
                this.generating = false;
                this.qrInfo = info;
                this.loadQrImage(info.qrCodeFileId);
                this._appNotification.showSuccess(this._tolgee.translate('bcmBerths.onlineBookingQr.generated'));
            },
            error: err => {
                this.generating = false;
                this._appNotification.showError(err?.error?.message || this._tolgee.translate('common.checkYourEntries'));
            },
        });
    }

    private loadQrImage(fileId?: number): void {
        this.revokeQrImageUrl();

        if (!fileId) {
            return;
        }

        this.blobSubscription?.unsubscribe();
        this.blobSubscription = this._filesApi.getById(fileId).subscribe(blob => {
            this.qrImageUrl = URL.createObjectURL(blob);
        });
    }

    private revokeQrImageUrl(): void {
        this.blobSubscription?.unsubscribe();
        this.blobSubscription = undefined;

        if (this.qrImageUrl) {
            URL.revokeObjectURL(this.qrImageUrl);
            this.qrImageUrl = undefined;
        }
    }
}
